<!--
  [1] Image sizes and ratios do not need to be defined here but in the corresponding files in the pages folder and inserted in this component.
      This way you can have any ratio you want in this card. -->

<template>
  <article class="w-full h-full" data-component="CardCover">
    <AtomLink :url="url" class="block relative cursor-pointer group w-full">
      <div class="grid grid-cols-12 w-full h-full rounded-2xl lg:rounded-3xl overflow-hidden shadow-md hover:shadow-none transition duration-200">
        <div class="row-span-full col-span-full w-full h-full relative z-0 v-gradient-card-cover">
          <MoleculePicture
            v-if="image"
            class="block w-full h-full transition duration-1000 group-hover:scale-105 transform object-cover"
            :class="{ 'aspect-[4/5]': portrait }"
            :image="image"
            :lazy="imageLazy"
            :aria-hidden="true"
          />
        </div>

        <div
          class="row-span-full col-span-full flex flex-col justify-end mt-10 p-4 sm:p-8 z-10 translate-y-1 group-hover:translate-y-0 transition-transform duration-1000 transform"
          :class="{
            'lg:p-12 md:col-start-1 md:col-end-10 xl:col-start-1 xl:col-end-7 leading-tight': size === 'large',
            'lg:p-12 xl:col-start-1 xl:col-end-11 leading-tight': size === 'medium',
            'xl:col-start-1 xl:col-end-11 leading-tight': size === 'landscape',
            'leading-tight': size === 'small',
            'leading-snug': size === 'extraSmall',
          }"
        >
          <h3
            v-if="title"
            class="text-white font-semibold v-text-shadow--1 order-2"
            :class="{
              'text-xl sm:text-2xl lg:text-3xl': size === 'large',
              'text-xl md:text-2xl lg:text-3xl': size === 'medium',
              'text-lg md:text-2xl lg:text-xl xl:text-3xl': size === 'landscape',
              'text-lg md:text-xl xl:text-2xl': !size || size === 'small',
              'text-sm sm:text-lg xl:text-lg': size === 'extraSmall',
              'mb-1 xl:mb-2': theme === 'violet' && subtitle,
            }"
          >
            {{ title }}
          </h3>
          <span
            v-if="subtitle"
            class="inline-flex items-center gap-x-2 mb-1 text-white font-normal v-text-shadow--1 order-1"
            :class="{
              'lg:text-lg font-normal': size === 'large' || size === 'medium',
              'xl:text-lg font-normal': size === 'landscape',
              'text-sm xl:text-lg font-normal': !size || size === 'small',
              'text-xs sm:text-sm xl:text-base font-medium sm:font-normal': size === 'extraSmall',
              'order-2': theme === 'violet',
            }"
          >
            <div
              v-if="duration"
              class="font-medium text-xs leading-none bg-app-primary-1-red--fire-brick px-2 py-1 z-20 text-white rounded-2xl inline-flex items-center justify-center gap-x-1"
            >
              <AtomIcon icon="app-play-triangle" classes="w-2 h-2 transition duration-300" />
              <span>{{ time }}</span>
            </div>
            <span>{{ subtitle }}</span>
          </span>
        </div>
      </div>
      <div
        v-if="iconRight"
        class="absolute top-0 right-0 p-3 md:p-3 z-20 bg-white rounded-bl-2xl lg:rounded-bl-3xl rounded-tr-2xl lg:rounded-tr-3xl flex items-center justify-center"
        :class="{
          'xl:w-16 xl:h-16': size === 'large' || size === 'medium' || size === 'landscape',
          'text-app-secondary-7-violet--purple-heart': theme === 'violet',
          'text-app-primary-1-red--fire-brick group-hover:text-app-primary-1-red--torch': theme !== 'violet',
        }"
      >
        <AtomIcon :icon="iconRight" classes="w-6 h-6 md:w-8 md:h-8 transition duration-300" />
      </div>

      <div
        v-if="iconLeft"
        class="absolute top-0 left-0 p-3 md:p-3 z-20 bg-white rounded-br-2xl lg:rounded-br-3xl rounded-tl-2xl lg:rounded-tl-3xl flex items-center justify-center"
      >
        <AtomIcon :icon="iconLeft" classes="w-24 h-6 md:w-40 md:h-8 transition duration-300 content-center" />
      </div>
    </AtomLink>
  </article>
</template>
<!--
    size: var to point out on which row the card is shown. The row determines the card size, i.e.; size 'large' shows the card larger than 'medium'.
    If no size is given, the card defaults to the smallest size and will be portrait style.
-->

<script setup lang="ts">
const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, required: false, default: undefined },
  iconLeft: { type: String, required: false, default: undefined },
  iconRight: { type: String, required: false, default: undefined },
  duration: { type: Number, required: false, default: undefined },
  image: { type: Object as PropType<Record<string, any>>, required: false, default: undefined },
  imageLazy: { type: Boolean, required: false, default: true },
  portrait: { type: Boolean, required: false, default: false },
  url: { type: String, required: false, default: undefined },
  size: { type: String, required: false, default: null, validator: (value: string) => ['large', 'medium', 'small', 'extraSmall', 'landscape'].includes(value) },
  theme: { type: String, required: false, default: undefined },
})

const time = computed(() => {
  const number = props.duration ?? 0

  // Extract hours, minutes, and seconds
  const hours = Math.floor(number / 3600)
  const remainingSeconds = number % 3600
  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60

  // Add leading zeros
  const formattedHours = ('0' + hours).slice(-2)
  const formattedMinutes = ('0' + minutes).slice(-2)
  const formattedSeconds = ('0' + seconds).slice(-2)

  // Return the formatted time string
  if (hours > 0) {
    return formattedHours + ':' + formattedMinutes + ':' + formattedSeconds
  } else {
    return formattedMinutes + ':' + formattedSeconds
  }
})
</script>

<style lang="scss" scoped>
.v-gradient-card-cover {
  &::after {
    @apply absolute block w-full h-full left-0 bottom-0;
    content: '';
    background:
      radial-gradient(circle at 5% 95%, rgba(99, 81, 61, 0.5) 0%, theme('colors.transparent') 80%),
      radial-gradient(circle at 5% 95%, rgba(99, 81, 61, 0.4) 0%, theme('colors.transparent') 30%);
  }
}
.v-text-shadow--1 {
  text-shadow: 0 0 7px theme('colors.black');
}
</style>
